import React from "react";
import { ProductColumnHeader } from "src/enums";
import { TableColumn, TransitProductAmendmentInputField } from "src/types";
import { hasAnyWarehouseSplit, splitByWarehouseRatio } from "src/helpers";
import { TextInputBox } from "src/components";
import { Table } from "src/components";

interface TransitProductAmendmentInputTableProps {
  productAmendments: TransitProductAmendmentInputField[];
  setProductAmendments: React.Dispatch<React.SetStateAction<TransitProductAmendmentInputField[]>>;
}

const TransitProductAmendmentInputTable: React.FC<TransitProductAmendmentInputTableProps> = ({
  productAmendments,
  setProductAmendments
}) => {
  const handleProductAmendmentChange = <T,>(
    id: string,
    fieldName: keyof TransitProductAmendmentInputField,
    value: T
  ) => {
    setProductAmendments(
      productAmendments.map((amendedProduct) =>
        amendedProduct.fieldId === id ? { ...amendedProduct, [fieldName]: value } : amendedProduct
      )
    );
  };

  const showWarehouseBreakdown = hasAnyWarehouseSplit(productAmendments);

  const productAmendmentTableColumns: TableColumn<ProductColumnHeader, TransitProductAmendmentInputField>[] = [
    {
      header: ProductColumnHeader.ID,
      column: (row: TransitProductAmendmentInputField) => (
        <span className="font-medium text-secondary-900">{row.id}</span>
      ),
      styles: { minWidth: "100px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row) => <span className="font-semibold text-secondary-900">{row.name || "-"}</span>,
      styles: { minWidth: "160px" }
    },
    {
      header: ProductColumnHeader.BARCODE,
      column: (row) => <span className="font-mono text-secondary-900">{row.barcode || "-"}</span>,
      styles: { minWidth: "140px" }
    },
    {
      header: ProductColumnHeader.SKU,
      column: (row) => <span className="text-secondary-700">{row.skuName || "-"}</span>,
      styles: { minWidth: "110px" }
    },
    {
      header: ProductColumnHeader.SIZE,
      column: (row) => <span className="text-secondary-700">{row.size || "-"}</span>,
      styles: { minWidth: "90px" }
    },
    {
      header: showWarehouseBreakdown ? (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs">Order Qty</span>
          <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
            <span className="text-violet-300">UK</span>
            <span className="text-orange-300">EU</span>
          </div>
        </div>
      ) : (
        ProductColumnHeader.ORDER_QUANTITY
      ),
      column: (row) =>
        showWarehouseBreakdown ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-semibold text-secondary-900 tabular-nums">{row.orderedQuantity ?? "-"}</span>
            <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
              <span className="text-right text-violet-600">{row.ukQuantity ?? 0}</span>
              <span className="text-left text-orange-600">{row.euQuantity ?? 0}</span>
            </div>
          </div>
        ) : (
          <span className="font-semibold text-secondary-900">{row.orderedQuantity ?? "-"}</span>
        ),
      styles: { minWidth: showWarehouseBreakdown ? "120px" : "100px", textAlign: "center" }
    },
    {
      header: showWarehouseBreakdown ? (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs">Confirmed Qty</span>
          <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
            <span className="text-violet-300">UK</span>
            <span className="text-orange-300">EU</span>
          </div>
        </div>
      ) : (
        ProductColumnHeader.CONFIRMED_QTY
      ),
      column: (row) => {
        if (!showWarehouseBreakdown) {
          return <span className="font-semibold text-secondary-900">{row.confirmedQuantity ?? "-"}</span>;
        }

        const { uk: confirmedUk, eu: confirmedEu } = splitByWarehouseRatio(
          row.confirmedQuantity,
          row.ukQuantity || 0,
          row.euQuantity || 0
        );

        return (
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-semibold text-secondary-900 tabular-nums">{row.confirmedQuantity ?? "-"}</span>
            {row.confirmedQuantity !== undefined && (
              <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
                <span className="text-right text-violet-600">{confirmedUk}</span>
                <span className="text-left text-orange-600">{confirmedEu}</span>
              </div>
            )}
          </div>
        );
      },
      styles: { minWidth: showWarehouseBreakdown ? "130px" : "100px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.WAREHOUSE_LOG_QTY,
      column: (row) => <span className="font-semibold text-secondary-900">{row.warehouseLogQuantity ?? "-"}</span>,
      styles: { minWidth: "100px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.CHILD_WAREHOUSE_LOG_QTY,
      column: (row) => <span className="font-semibold text-secondary-900">{row.childWarehouseLogQuantity ?? "-"}</span>,
      styles: { minWidth: "150px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.RECEIVED_QTY,
      column: (row) => (
        <div className="w-full">
          <TextInputBox
            id={`received-qty-${row.fieldId}`}
            value={(
              productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)?.amendedReceivedQty ??
              ""
            ).toString()}
            onChange={(value) => handleProductAmendmentChange(row.fieldId, "amendedReceivedQty", value)}
            maxLength={10}
            placeholder={`/ ${row.orderReceivedQty}`}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "110px" }
    }
  ];

  return <Table data={productAmendments} columns={productAmendmentTableColumns} />;
};

export { TransitProductAmendmentInputTable };
